import React, {
  useContext,
  useEffect,
  useState,
} from "react";
import AuthContext from "../Context/AuthProvider";
import {
  convertToUTC,
  convertUTCToTimeZoneDateTimeString,
} from "../Utility/DateTimeUtility";
import {
  DateRangeOptions,
  ServiceStatus,
} from "../Utility/Constants";
import {
  getDisplayValue,
  formatAge,
} from "../Utility/Utility";
import MoreVert from "@mui/icons-material/MoreVert";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Grid2,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import { LocalParkingRounded } from "@mui/icons-material";
import dayjs from "dayjs";
import { getAllAssignedServices } from "../API/AllAssignedServicesApi";
import { useSnackbar } from "../Utility/UserMessageContext";
import DateRangeDialog from "../Appointments/ChangeDates";
import MenuBar from "../common/MenuBar";
import { UserRolesConstants } from "../Utility/Constants";
import { EventBusy } from "@mui/icons-material";

function AllTasks({
  onViewDetails,
  onViewVisitDetails,
  onViewPatientDetails,
}) {
  const { auth } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [allServices, setAllServices] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [openDateRangeDialog, setOpenDateRangeDialog] = useState(false);
  const isAdmin = auth?.roles?.includes(UserRolesConstants.FacilityAdmin);

  const [filters, setFilters] = useState({
    from: dayjs(new Date()),
    to: dayjs(new Date()),
    selectedDateRangeOption: DateRangeOptions.Today,
  });

  const { showSnackbar } = useSnackbar();

  const handleActionsMenuOpen = (event, row) => {
    setActionsMenuAnchor(event.currentTarget);
    setCurrentRow(row);
  };

  const handleMenuClose = () => {
    setActionsMenuAnchor(null);
    setCurrentRow(null);
  };

  const fetchData = async () => {
    setShowSpinner(true);
    setDataLoaded(false);

    const result = await getAllAssignedServices(
      auth,
      convertToUTC(
        dayjs(filters.from)
          .set("hour", 0)
          .set("minute", 0)
          .set("second", 0)
          .set("millisecond", 0),
      ),
      convertToUTC(
        dayjs(filters.to)
          .set("hour", 23)
          .set("minute", 59)
          .set("second", 59)
          .set("millisecond", 999),
      ),
    );

    if (result.isSuccess) {
      setAllServices(result?.data || []);
    } else {
      setAllServices([]);
      showSnackbar(result?.data, "error");
    }

    setDataLoaded(true);
    setShowSpinner(false);
  };

  useEffect(() => {
    fetchData();
  }, [auth, filters]);

  const getNoRowsOverlay = () => {
    if (showSpinner || !dataLoaded) return "";
    return "There are no services to display. Change the dates to view all tasks.";
  };

  // ── Mobile Card View ──────────────────────────────────────────────────────
  const renderServiceCards = () => (
    <Grid2
      container
      spacing={2}
      sx={{
        width: "100%",
        maxHeight: "calc(100vh - 170px)",
        overflowY: "auto",
      }}
    >
      <Box sx={{ px: 1 }}>
        {allServices.map((service) => (
          <Card
            key={service.id}
            variant="outlined"
            sx={{ mb: 2, borderRadius: 2 }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 1,
                }}
              >
                <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1 }}>
                  {`${service.patientPrefix || ""} ${service.patientFirstName || ""} ${service.patientLastName || ""}`.trim()}
                </Typography>
                <Chip
                  label={getDisplayValue(service.status)}
                  color={
                    service.status === ServiceStatus.Completed
                      ? "success"
                      : service.status === ServiceStatus.InProgress
                        ? "primary"
                        : "default"
                  }
                  size="small"
                />
              </Box>

              <Divider sx={{ my: 1 }} />

              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>ID:</strong> {service.displayId}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Gender:</strong> {service.patientGender}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Age:</strong> {formatAge(service.patientAgeInYears)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Contact:</strong> {service.patientMobileNo}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Email:</strong> {service.patientEmailId}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Service:</strong> {service.serviceName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Assigned To:</strong> {service.assignedToUserName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Scheduled:</strong>{" "}
                {convertUTCToTimeZoneDateTimeString(
                  service.scheduledStartDateTime,
                  auth?.selectedFacility?.timeZone,
                )}
              </Typography>
            </CardContent>

            {/* No action buttons — view details only via menu or tap */}
            <Box sx={{ px: 2, pb: 2, display: "flex", justifyContent: "flex-end" }}>
              <Tooltip title="View Details" arrow placement="top">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => onViewDetails(service)}
                >
                  <EventNoteOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Card>
        ))}

        {dataLoaded && allServices.length === 0 && !showSpinner && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              There are no services to display. Change the dates to view all tasks.
            </Typography>
          </Box>
        )}

        {showSpinner && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        )}
      </Box>
    </Grid2>
  );

  // ── DataGrid columns ──────────────────────────────────────────────────────
  const columns = [
    {
      field: "displayId",
      headerName: "ID",
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span
            style={{ textDecoration: "underline", cursor: "pointer" }}
            onClick={() => onViewDetails(params.row)}
          >
            {params.row.displayId}
          </span>
          <Tooltip title="Actions" arrow>
            <IconButton
              size="small"
              onClick={(event) => handleActionsMenuOpen(event, params.row)}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "visitDisplayId",
      headerName: "Visit #",
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span
            style={{ textDecoration: "underline", cursor: "pointer" }}
            onClick={() =>
              onViewVisitDetails &&
              onViewVisitDetails(params.row.visitId, params.row.visitDisplayId)
            }
          >
            {params.row.visitDisplayId}
          </span>
          {params?.row?.isLinkedWithPlan && (
            <Tooltip title="Linked to Plan" arrow>
              <LocalParkingRounded fontSize="4" />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: "patient",
      headerName: "Name",
      width: 220,
      renderCell: (params) => {
        const patientPrefix = params.row.patientPrefix || "";
        const firstName = params.row.patientFirstName || "";
        const lastName = params.row.patientLastName || "";
        const gender = params.row.patientGender || "";
        const age = params.row.patientAgeInYears || "";
        const phone = params.row.patientMobileNo || "";
        const countryCode = params?.row?.countryCodeForPhone || "";

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: "100%",
              lineHeight: 1.3,
            }}
          >
            <div>
              <span
                style={{
                  fontWeight: "bold",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => {
                  const patientData = {
                    id: params.row.patientId,
                    firstName: params.row.patientFirstName,
                    lastName: params.row.patientLastName,
                    prefix: params.row.patientPrefix,
                    gender: params.row.patientGender,
                    ageInYears: params.row.patientAgeInYears,
                    mobileNo: params.row.patientMobileNo,
                    emailId: params.row.patientEmailId,
                  };
                  onViewPatientDetails(patientData);
                }}
              >
                {patientPrefix} {firstName} {lastName}
              </span>
              <span style={{ fontWeight: "normal" }}> ({gender})</span>
            </div>
            <span style={{ color: "#555", fontSize: "0.85em" }}>
              {`${countryCode} ${phone} (${formatAge(age)})`}
            </span>
          </div>
        );
      },
    },
    {
      field: "assignedToUserName",
      headerName: "Assigned To",
      width: 180,
    },
    {
      field: "serviceName",
      headerName: "Service",
      width: 180,
      renderCell: (params) => params?.row?.primaryServiceName || params?.row?.serviceName,
    },
    {
      field: "scheduledStartDateTime",
      headerName: "Scheduled Time",
      width: 220,
      valueGetter: (params) =>
        convertUTCToTimeZoneDateTimeString(params, auth?.selectedFacility?.timeZone),
    },
    {
      field: "status",
      headerName: "Status",
      width: 180,
      renderCell: (params) => getDisplayValue(params?.row?.status),
    },
  ];

  return (
    <>
    
      <Grid2 container size={12}>
        <Grid2 container size={12}>
          <Grid2 size={12}>
            <MenuBar
              showChangeDate={true}
              onChangeDate={() => setOpenDateRangeDialog(true)}
              disabled={false}
            />
          </Grid2>
          <Grid2 size={12} sx={{ px: 1, py: 1 }}>
            <Typography
              variant="body1"
              sx={{
                fontSize: {
                  xs: "0.75rem",
                  sm: "0.875rem",
                  md: "1rem",
                  lg: "1.125rem",
                  xl: "1.25rem",
                },
              }}
            >
              Showing all tasks from {filters?.from?.format("DD/MM/YYYY")} to{" "}
              {filters?.to?.format("DD/MM/YYYY")}
            </Typography>
          </Grid2>
        </Grid2>

        {isMobile ? (
          <Box sx={{ width: "100%", paddingX: "5px" }}>
            {renderServiceCards()}
          </Box>
        ) : (
          <Grid2
            container
            size={12}
            sx={{
              maxHeight: "calc(100vh - 170px)",
              overflowY: "auto",
              display: "flex",
              width: "100%",
              paddingX: "5px",
              height: 600,
            }}
          >
            <DataGrid
              localeText={{ noRowsLabel: getNoRowsOverlay() }}
              loading={showSpinner || !dataLoaded}
              slotProps={{
                loadingOverlay: {
                  variant: "skeleton",
                  noRowsVariant: "skeleton",
                },
              }}
              initialState={{
                density: "compact",
                pagination: { paginationModel: { pageSize: 20 } },
              }}
              sx={{
                width: "200px",
                overflow: "hidden",
                "& .MuiTablePagination-toolbar": {
                  minHeight: "unset",
                  alignItems: "center",
                  paddingTop: "0 !important",
                  paddingBottom: "0 !important",
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  margin: 0,
                },
              }}
              rowHeight={65}
              rows={allServices}
              columns={columns}
              pageSizeOptions={[5, 10, 15, 20]}
              rowsPerPageOptions={[5, 10, 15, 20]}
            />
          </Grid2>
        )}

        {/* Actions Menu — view details only, no status actions */}
        <Menu
          anchorEl={actionsMenuAnchor}
          open={Boolean(actionsMenuAnchor)}
          onClose={handleMenuClose}
        >
          {currentRow && (
            <MenuItem
              onClick={() => {
                onViewDetails(currentRow);
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <EventNoteOutlinedIcon fontSize="small" />
              </ListItemIcon>
              View Details
            </MenuItem>
          )}
        </Menu>
      </Grid2>

      <DateRangeDialog
        key="alltasks"
        title="Show All Tasks For"
        filters={filters}
        setFilters={setFilters}
        openState={openDateRangeDialog}
        setOpenState={setOpenDateRangeDialog}
      />
    </>
  );
}

export default AllTasks;